import { memo, useCallback, useEffect, useState } from 'react';

import { PlanningEntityType } from 'generated';
import { useSelector } from 'hooks';
import { useRecoilValue } from 'recoil';
import dayjs from 'dayjs';

import { PlanningEntity } from '../../../types';

import { getItemWidthInPixels, getStartingPoint } from '../../utils';

import { appointmentDropPositionXAtom } from '../../atoms';

import { Box } from '@mui/material';

import type { Interval } from '../../../hooks/useTimelineInterval';
import {
  AppointmentChip,
  ConditionalDraggableChip
} from '../../AppointmentChip';
import { MultiDragOverlay } from '../../MultiDragOverlay';

import TravelTime from '../TravelTime';
import { ZoomLevel } from '../../TimelineBar';
import AppointmentContext from '../../AppointmentContext';
import { getEmployeeName } from 'components/roster-portal/activity-planning/utils';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { useTranslation } from 'react-i18next';

type Props = {
  activeEntityContainingSelection: string | null;
  connectAppointmentGrouping: (
    appointmentGroupingEntityId: string,
    editAppointmentType?: EditAppointmentType,
    scheduleStart?: string
  ) => void;
  planningEntity: PlanningEntity;
  planningEntityType?: PlanningEntityType;
  onChangeMousePosition?: (pos: { x: number; y: number }) => void;
  onChangeActiveEntity?: (entityId: string) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
  scheduleStart?: string;
  interval: Interval;
  zoomLevel: ZoomLevel;
  timeBarItemWidth: number;
};

const Appointments = memo(
  ({
    activeEntityContainingSelection,
    connectAppointmentGrouping,
    interval,
    onChangeMousePosition,
    onChangeActiveEntity,
    onChangeSelection,
    planningEntity,
    planningEntityType,
    zoomLevel,
    timeBarItemWidth
  }: Props) => {
    const { planningEntityIdentifier, schedules, absences, availabilities } =
      planningEntity;
    const planningEntityId =
      planningEntityIdentifier?.entityId ??
      planningEntityIdentifier?.externalEntityId ??
      '';

    const { hasActivityPlanningEditAccess } = useSelector(
      ({ grants }) => grants
    );
    const appointmentDropPositionX = useRecoilValue(
      appointmentDropPositionXAtom
    );

    const { t } = useTranslation('common', { keyPrefix: 'activityPlanning' });

    const [mousePosition, setMousePosition] = useState<{
      x: number;
      y: number;
    }>({
      x: 0,
      y: 0
    });

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const isActiveEntity: boolean =
      activeEntityContainingSelection === planningEntityId; // The active route

    const { start: scheduleStart } = schedules?.[0] || {};
    const events = schedules?.map((s) => s.events).flatMap((e) => e);
    const appointments = events?.filter(
      ({ category }) => category !== 'DROPZONE'
    );

    const onHandleSelect = useCallback((id: string) => {
      setSelectedIds((selectedIds) => {
        // Call onChangeActiveEntity from parent component so it may update activeEntityContainingSelection
        onChangeActiveEntity?.(planningEntityId);

        if (selectedIds.includes(id))
          return selectedIds.filter((value) => value !== id);
        if (!selectedIds.length) return [id];
        return selectedIds.concat(id);
      });
    }, []);

    // If the active entity (the route) is *not* the entity containing the selected appointments, clear the selection
    useEffect(() => {
      if (!isActiveEntity && selectedIds.length > 0) setSelectedIds([]);
    }, [isActiveEntity]);

    // If the selection changes, notify the parent
    useEffect(() => {
      onChangeSelection?.(selectedIds);
    }, [selectedIds]);

    return (
      <>
        {appointments?.map((props, i: number) => {
          const key = `${i}-${props.id}-${props.start}`;

          const startingPoint = getStartingPoint(
            props.start,
            interval,
            zoomLevel,
            timeBarItemWidth
          );

          const widthInPixels = getItemWidthInPixels(
            timeBarItemWidth,
            props.durationInSeconds,
            zoomLevel
          );

          const appointmentWidthInPixels = Math.max(4, widthInPixels);

          if (!widthInPixels) return null;

          if (props.category === 'TRAVELTIME') {
            return (
              <TravelTime
                key={`${key}-traveltime`}
                width={widthInPixels}
                durationInSeconds={props.durationInSeconds}
                distanceInMeters={props.location?.distanceInMeters}
                startingPoint={startingPoint}
              />
            );
          }

          if (props.category === 'FREE') {
            return null;
          }

          if (props.category === 'MULTIPLE') {
            return (
              <Box
                key={`${key}-multiple`}
                onMouseDown={(e) => {
                  setMousePosition({ x: e.clientX, y: e.clientY });
                  onChangeMousePosition?.({ x: e.clientX, y: e.clientY });
                }}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: appointmentDropPositionX?.[props.id]
                }}
              >
                <MultiDragOverlay
                  id={props.id}
                  count={props.count}
                  mousePosition={mousePosition}
                />
              </Box>
            );
          }

          if (!hasActivityPlanningEditAccess || props.isAbsent) {
            return (
              <Box
                key={`${key}-appointmentchip`}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: startingPoint,
                  zIndex: props.isAbsent ? 1 : 2
                }}
              >
                <AppointmentChip {...props} width={appointmentWidthInPixels} />
              </Box>
            );
          }

          return (
            <ConditionalDraggableChip
              key={`${key}-conditionaldraggablechip`}
              width={appointmentWidthInPixels}
              startingPoint={startingPoint}
              appointmentDropPositionX={appointmentDropPositionX}
              planningEntityId={planningEntityId}
              planningEntityType={planningEntityType}
              selected={selectedIds.includes(props.id)}
              onSelectAppointment={onHandleSelect}
              isOfMultipleSelection={
                selectedIds.length > 1 && selectedIds.includes(props.id)
              }
              scheduleStart={scheduleStart}
              onChangeMousePosition={onChangeMousePosition}
              isToPlan={false}
              {...props}
            />
          );
        })}
        {/* ABSENCES */}
        {absences?.map((props, i: number) => {
          const key = `${i}-${props.start}-absence`;

          const absenceStartsDayBefore = dayjs(props.start).isBefore(
            dayjs(interval.start)
          );
          const absenceEndsDayAfter = dayjs(props.end).isAfter(
            dayjs(interval.end)
          );

          const start = absenceStartsDayBefore
            ? dayjs(interval.start)
            : dayjs(props.start);
          const end = absenceEndsDayAfter
            ? dayjs(interval.end)
            : dayjs(props.end);
          const durationInSeconds = end.diff(start, 'seconds');
          const startString = absenceStartsDayBefore
            ? dayjs(props.start).format('D MMMM YYYY HH:mm')
            : dayjs(props.start).format('HH:mm');
          const endString = absenceEndsDayAfter
            ? dayjs(props.end).format('D MMMM YYYY HH:mm')
            : dayjs(props.end).format('HH:mm');

          const startingPoint = getStartingPoint(
            start.toISOString(),
            interval,
            zoomLevel,
            timeBarItemWidth
          );
          const widthInPixels = getItemWidthInPixels(
            timeBarItemWidth,
            durationInSeconds,
            zoomLevel
          );

          return (
            <AppointmentContext
              key={key}
              label={`Afwezig van ${startString} tot ${endString}`}
              absence={props}
              type="absence"
              width={widthInPixels}
              startingPoint={startingPoint}
            />
          );
        })}
        {/* AVAILABILITIES */}
        {availabilities?.map((props, i: number) => {
          const key = `${i}-${props.start}-availability`;

          const availabilitiesStartsDayBefore = dayjs(props.start).isBefore(
            dayjs(interval.start)
          );
          const availabilitiesEndsDayAfter = dayjs(props.end).isAfter(
            dayjs(interval.end)
          );
          const start = availabilitiesStartsDayBefore
            ? dayjs(interval.start)
            : dayjs(props.start);
          const end = availabilitiesEndsDayAfter
            ? dayjs(interval.end)
            : dayjs(props.end);
          const durationInSeconds = end.diff(start, 'seconds');
          const startingPoint = getStartingPoint(
            start.toISOString(),
            interval,
            zoomLevel,
            timeBarItemWidth
          );
          const widthInPixels = getItemWidthInPixels(
            timeBarItemWidth,
            durationInSeconds,
            zoomLevel
          );
          const startTime = dayjs(props.start).format('HH:mm');
          const endTime = dayjs(props.end).format('HH:mm');
          const isUnavailability = props.type === 'NOT_AVAILABLE';

          return (
            <AppointmentContext
              key={key}
              label={
                isUnavailability
                  ? t('unavailableFromTilTimeline', {
                      from: startTime,
                      to: endTime
                    })
                  : t('availableFromTilTimeline', {
                      from: startTime,
                      to: endTime
                    })
              } /*Vertaling toevoegen*/
              type={isUnavailability ? 'unavailability' : 'availability'}
              width={widthInPixels}
              startingPoint={startingPoint}
              availability={props}
              connectAppointmentGrouping={connectAppointmentGrouping}
            />
          );
        })}
        {/* SCHEDULES */}
        {schedules?.map((schedule, i: number) => {
          const { employee } = schedule;
          const start = dayjs(schedule.start).format('HH:mm');

          const key = `${i}-${start}-schedule`;

          const startingPoint = getStartingPoint(
            schedule.start,
            interval,
            zoomLevel,
            timeBarItemWidth
          );

          const widthInPixels = getItemWidthInPixels(
            timeBarItemWidth,
            dayjs(schedule.end).diff(schedule.start, 'seconds'),
            zoomLevel
          );

          const isAvailability = schedule.type === 'AVAILABILITY';
          const isOtherSchedule = schedule.type === 'OTHER'; // temp

          if (isAvailability) {
            return (
              <AppointmentContext
                key={key}
                label={schedule.name || ''}
                type="availability"
                width={widthInPixels}
                startingPoint={startingPoint}
                schedule={schedule}
                connectAppointmentGrouping={connectAppointmentGrouping}
              />
            );
          }

          if (isOtherSchedule) {
            return (
              <AppointmentContext
                key={key}
                label={'Geen dienst'}
                type="none"
                width={widthInPixels}
                startingPoint={startingPoint}
                schedule={schedule}
                connectAppointmentGrouping={connectAppointmentGrouping}
              />
            );
          }

          return (
            <AppointmentContext
              key={key}
              label={
                planningEntityType === 'ROUTE'
                  ? getEmployeeName(employee) || 'Geen medewerker'
                  : schedule.name || ''
              }
              type="schedule"
              width={widthInPixels}
              startingPoint={startingPoint}
              schedule={schedule}
              connectAppointmentGrouping={connectAppointmentGrouping}
            />
          );
        })}
      </>
    );
  }
);

export default Appointments;
